import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArchive,
  IconArrowBigUpLines,
  IconCheck,
  IconCopy,
  IconKey,
  IconLogin2,
  IconRestore,
  IconTrash,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

function VendorDetail() {
  const { uid } = useParams({ from: '/central/vendors/$uid' });
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const v = trpc.tenancy.vendors.getByUid.useQuery({ uid });
  const list = trpc.tenancy.vendors.list.useQuery({ take: 200 });
  const adminLink = list.data?.vendors.find((row) => row.uid === uid)?.admin ?? null;

  const metrics = trpc.tenancy.vendors.metrics.useQuery(
    { vendorId: v.data?.id ?? -1 },
    { enabled: !!v.data?.id },
  );

  const vendorId = v.data?.id ?? -1;
  const featureList = trpc.admin.vendorFeatures.list.useQuery(
    { vendorId },
    { enabled: !!v.data?.id },
  );
  const setFeature = trpc.admin.vendorFeatures.set.useMutation({
    onSuccess: () => {
      void utils.admin.vendorFeatures.list.invalidate({ vendorId });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const subList = trpc.billing.subscriptions.list.useQuery({ take: 1 }, { enabled: !!v.data?.id });
  // Explicit cast — breaks tsc's deep recursion (TS2589).
  interface SubRow {
    id: number;
    vendorId: number;
    status: string;
    planId: number;
    plan: { name: string } | null;
    gateway: string;
    currentPeriodStart: string | Date;
    currentPeriodEnd: string | Date;
    cancelAtPeriodEnd: boolean;
  }
  const subRows = (subList.data ?? []) as readonly SubRow[];
  const sub = subRows.find((s) => s.vendorId === v.data?.id) ?? null;

  const update = trpc.tenancy.vendors.update.useMutation({
    onSuccess: () => {
      void utils.tenancy.vendors.getByUid.invalidate({ uid });
      notifications.show({ message: 'Vendor saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const archive = trpc.tenancy.vendors.archive.useMutation({
    onSuccess: () => {
      void utils.tenancy.vendors.getByUid.invalidate({ uid });
      notifications.show({ message: 'Vendor archived', color: 'orange' });
    },
  });
  const unarchive = trpc.tenancy.vendors.unarchive.useMutation({
    onSuccess: () => {
      void utils.tenancy.vendors.getByUid.invalidate({ uid });
      notifications.show({ message: 'Vendor reactivated', color: 'green' });
    },
  });
  const loginAs = trpc.auth.startImpersonationByVendor.useMutation({
    onSuccess: async () => {
      await utils.auth.me.invalidate();
      navigate({ to: '/vendor' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const adminUpdate = trpc.auth.adminUpdateUser.useMutation({
    onSuccess: () => {
      void utils.tenancy.vendors.list.invalidate();
      notifications.show({ message: 'Admin updated', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const resetPwd = trpc.auth.adminResetPassword.useMutation({
    onSuccess: (r) => {
      if (r.password) {
        setGeneratedPassword(r.password);
        passwordModalH.open();
      } else {
        notifications.show({ message: 'Password updated', color: 'green' });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const adminAdjust = trpc.billing.subscriptions.adminAdjust.useMutation({
    onSuccess: () => {
      void utils.billing.subscriptions.list.invalidate();
      notifications.show({ message: 'Subscription adjusted', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const hardDelete = trpc.tenancy.vendors.hardDelete.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Vendor permanently deleted', color: 'red' });
      navigate({ to: '/central/vendors' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // Local edit state
  const [title, setTitle] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminMobile, setAdminMobile] = useState('');
  useEffect(() => {
    if (v.data) setTitle(v.data.title);
  }, [v.data]);
  useEffect(() => {
    if (adminLink) {
      setAdminFirstName(adminLink.firstName ?? '');
      setAdminLastName(adminLink.lastName ?? '');
      setAdminEmail(adminLink.email);
      setAdminMobile(adminLink.mobileNumber ?? '');
    }
  }, [adminLink]);

  // Hard delete confirmation
  const [confirmSlug, setConfirmSlug] = useState('');
  const [deleteModalOpen, deleteModalH] = useDisclosure(false);

  // Password reset modal state
  const [passwordModalOpen, passwordModalH] = useDisclosure(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  // Subscription extend modal
  const [extendModalOpen, extendModalH] = useDisclosure(false);
  const [extendDays, setExtendDays] = useState<number | ''>(30);

  if (v.isPending) return <Skeleton height={400} />;
  if (v.isError || !v.data) return <Text c="red">{v.error?.message ?? 'Not found'}</Text>;

  const vendor = v.data;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <Stack gap={2}>
            <Title order={2}>{vendor.title}</Title>
            <Group gap={6}>
              <Text c="dimmed" size="sm" ff="monospace">
                {vendor.slug}
              </Text>
              <Badge color={vendor.status === 'Active' ? 'green' : 'gray'} variant="light">
                {vendor.status}
              </Badge>
            </Group>
          </Stack>
          <Group>
            <Button
              variant="light"
              leftSection={<IconLogin2 size={14} />}
              loading={loginAs.isPending}
              disabled={!adminLink || vendor.status !== 'Active'}
              onClick={() => loginAs.mutate({ vendorId: vendor.id })}
            >
              Login as Vendor Admin
            </Button>
            {vendor.status === 'Active' ? (
              <Button
                variant="default"
                color="orange"
                leftSection={<IconArchive size={14} />}
                onClick={() => archive.mutate({ vendorId: vendor.id })}
              >
                Archive
              </Button>
            ) : (
              <Button
                variant="default"
                leftSection={<IconRestore size={14} />}
                onClick={() => unarchive.mutate({ vendorId: vendor.id })}
              >
                Reactivate
              </Button>
            )}
          </Group>
        </Group>

        {/* Metrics */}
        <Grid gap="md">
          {[
            { label: 'Contacts', value: metrics.data?.contacts },
            { label: 'Team', value: metrics.data?.teamMembers },
            { label: 'Groups', value: metrics.data?.groups },
            { label: 'Campaigns', value: metrics.data?.campaignsSent },
            { label: 'Sent', value: metrics.data?.messagesSent },
            { label: 'Received', value: metrics.data?.messagesReceived },
            { label: 'Templates', value: metrics.data?.activeTemplates },
            {
              label: 'Last active',
              value: metrics.data?.lastActiveAt
                ? new Date(metrics.data.lastActiveAt).toLocaleDateString()
                : '—',
            },
          ].map((kpi) => (
            <Grid.Col key={kpi.label} span={{ base: 6, sm: 3 }}>
              <Card withBorder>
                <Text c="dimmed" size="xs">
                  {kpi.label}
                </Text>
                <Title order={3} mt={2}>
                  {metrics.isPending ? '—' : (kpi.value ?? 0).toString()}
                </Title>
              </Card>
            </Grid.Col>
          ))}
        </Grid>

        {/* General edit */}
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>General</Title>
            <TextInput
              label="Business name"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <Group>
              <Button
                loading={update.isPending}
                disabled={title === vendor.title || !title.trim()}
                onClick={() => update.mutate({ vendorId: vendor.id, title })}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Admin user */}
        <Card withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Title order={5}>Admin user</Title>
              {adminLink && (
                <Button
                  size="xs"
                  variant="light"
                  color="orange"
                  leftSection={<IconKey size={12} />}
                  onClick={() => resetPwd.mutate({ userId: adminLink.id })}
                  loading={resetPwd.isPending}
                >
                  Reset password
                </Button>
              )}
            </Group>
            {!adminLink ? (
              <Alert color="orange" icon={<IconAlertTriangle size={14} />}>
                This vendor has no admin user. Login-as is disabled until one is created.
              </Alert>
            ) : (
              <>
                <Group grow>
                  <TextInput
                    label="First name"
                    value={adminFirstName}
                    onChange={(e) => setAdminFirstName(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Last name"
                    value={adminLastName}
                    onChange={(e) => setAdminLastName(e.currentTarget.value)}
                  />
                </Group>
                <TextInput
                  label="Email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.currentTarget.value)}
                />
                <TextInput
                  label="Mobile"
                  value={adminMobile}
                  onChange={(e) => setAdminMobile(e.currentTarget.value)}
                />
                <Group>
                  <Button
                    loading={adminUpdate.isPending}
                    onClick={() =>
                      adminUpdate.mutate({
                        userId: adminLink.id,
                        firstName: adminFirstName,
                        lastName: adminLastName || null,
                        email: adminEmail,
                        mobile: adminMobile || null,
                      })
                    }
                  >
                    Save admin
                  </Button>
                  <Text size="xs" c="dimmed">
                    Last login:{' '}
                    {adminLink.lastLoginAt
                      ? new Date(adminLink.lastLoginAt).toLocaleString()
                      : 'never'}
                  </Text>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {/* Subscription */}
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>Subscription</Title>
            {subList.isPending ? (
              <Skeleton height={60} />
            ) : !sub ? (
              <Text c="dimmed" size="sm">
                No subscription on file.
              </Text>
            ) : (
              <>
                <Group gap="md" wrap="wrap">
                  <Badge
                    size="lg"
                    variant="light"
                    color={
                      sub.status === 'active'
                        ? 'green'
                        : sub.status === 'past_due'
                          ? 'yellow'
                          : 'gray'
                    }
                  >
                    {sub.status}
                  </Badge>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {sub.plan?.name ?? 'Plan ' + sub.planId}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {sub.gateway} · period {new Date(sub.currentPeriodStart).toLocaleDateString()}{' '}
                      → {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                      {sub.cancelAtPeriodEnd ? ' (cancelling)' : ''}
                    </Text>
                  </Stack>
                </Group>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconArrowBigUpLines size={12} />}
                    onClick={extendModalH.open}
                  >
                    Extend
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() =>
                      adminAdjust.mutate({
                        subscriptionId: sub.id,
                        action: 'cancel_at_period_end',
                      })
                    }
                    disabled={sub.cancelAtPeriodEnd}
                  >
                    Cancel at period end
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    color="red"
                    onClick={() =>
                      adminAdjust.mutate({
                        subscriptionId: sub.id,
                        action: 'cancel_immediate',
                      })
                    }
                  >
                    Cancel now
                  </Button>
                  {(sub.cancelAtPeriodEnd || sub.status !== 'active') && (
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      onClick={() =>
                        adminAdjust.mutate({
                          subscriptionId: sub.id,
                          action: 'reactivate',
                        })
                      }
                    >
                      Reactivate
                    </Button>
                  )}
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {/* Features (platform-managed) */}
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>Features</Title>
            <Text size="xs" c="dimmed">
              Enable or disable features for this vendor. Gated features depend on the
              platform&apos;s Meta approvals; the vendor cannot change these themselves.
            </Text>
            {featureList.isPending ? (
              <Skeleton height={80} />
            ) : (
              (featureList.data ?? []).map((f) => (
                <Group key={f.key} justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap="xs">
                      <Text fw={500} size="sm">
                        {f.label}
                      </Text>
                      {f.gated && (
                        <Badge size="xs" variant="light" color="yellow">
                          Meta approval
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {f.description}
                    </Text>
                  </Stack>
                  <Switch
                    checked={f.enabled}
                    disabled={setFeature.isPending}
                    onChange={(e) =>
                      setFeature.mutate({
                        vendorId,
                        key: f.key,
                        enabled: e.currentTarget.checked,
                      })
                    }
                  />
                </Group>
              ))
            )}
          </Stack>
        </Card>

        {/* Identifiers */}
        <Card withBorder>
          <Stack gap="xs">
            <Title order={5}>Identifiers</Title>
            <KV label="Vendor ID" value={String(vendor.id)} />
            <KV label="UID" value={vendor.uid} />
            <KV label="Slug" value={vendor.slug} />
            <KV label="Created" value={new Date(vendor.createdAt).toLocaleString()} />
          </Stack>
        </Card>

        {/* Danger zone */}
        <Card withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Stack gap="sm">
            <Group justify="space-between">
              <Stack gap={2}>
                <Title order={5} c="red">
                  Danger zone
                </Title>
                <Text size="sm" c="dimmed">
                  Permanently delete this vendor and every associated record (contacts, campaigns,
                  messages, subscriptions, settings, integrations). Required for Meta data-deletion
                  compliance — cannot be undone.
                </Text>
              </Stack>
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={14} />}
                onClick={deleteModalH.open}
              >
                Delete permanently
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>

      {/* Hard-delete confirmation modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          deleteModalH.close();
          setConfirmSlug('');
        }}
        title={`Permanently delete ${vendor.slug}?`}
        centered
      >
        <Stack>
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            This deletes the vendor and ALL associated data: contacts, campaigns, messages,
            subscriptions, integrations, settings. The admin user will be removed if not linked to
            other vendors. This cannot be undone.
          </Alert>
          <TextInput
            label={`Type '${vendor.slug}' to confirm`}
            value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.currentTarget.value)}
            placeholder={vendor.slug}
          />
          <Group justify="end">
            <Button variant="default" onClick={deleteModalH.close}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={hardDelete.isPending}
              disabled={confirmSlug !== vendor.slug}
              onClick={() => hardDelete.mutate({ vendorId: vendor.id, confirmSlug })}
            >
              Delete vendor permanently
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Password generated modal */}
      <Modal
        opened={passwordModalOpen}
        onClose={() => {
          passwordModalH.close();
          setGeneratedPassword('');
        }}
        title="Password reset"
        centered
        closeOnClickOutside={false}
      >
        <Stack>
          <Alert color="orange">
            Copy the new password now — it won&apos;t be shown again. Existing sessions for this
            user have been invalidated.
          </Alert>
          <Group>
            <Code style={{ flex: 1 }}>{generatedPassword}</Code>
            <CopyButton value={generatedPassword} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'}>
                  <ActionIcon variant="subtle" onClick={copy} color={copied ? 'green' : 'gray'}>
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Group justify="end">
            <Button
              onClick={() => {
                passwordModalH.close();
                setGeneratedPassword('');
              }}
            >
              I&apos;ve saved it
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Extend subscription modal */}
      <Modal
        opened={extendModalOpen}
        onClose={extendModalH.close}
        title="Extend subscription"
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Add days to the current period end. Useful for goodwill credits or covering a manual
            payment delay.
          </Text>
          <NumberInput
            label="Days to add"
            min={1}
            max={365}
            value={extendDays}
            onChange={(v) => setExtendDays(typeof v === 'number' ? v : '')}
          />
          <Group justify="end">
            <Button variant="default" onClick={extendModalH.close}>
              Cancel
            </Button>
            <Button
              loading={adminAdjust.isPending}
              disabled={typeof extendDays !== 'number' || extendDays < 1}
              onClick={() => {
                if (sub && typeof extendDays === 'number') {
                  adminAdjust.mutate({
                    subscriptionId: sub.id,
                    action: 'extend',
                    extendDays,
                  });
                  extendModalH.close();
                }
              }}
            >
              Extend
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between">
      <Text c="dimmed" size="sm">
        {label}
      </Text>
      <Text size="sm" ff="monospace">
        {value}
      </Text>
    </Group>
  );
}

export const Route = createFileRoute('/central/vendors/$uid')({ component: VendorDetail });
